/**
 * Altitude's contact details, in one place.
 *
 * WHY THIS EXISTS
 * ---------------
 * The phone numbers were written out in ten files - the header bar, the
 * footer, the contact page, the assessment page, the legal page, six database
 * error messages, the email footer, the Blueprint sign-off, the newsletter
 * sign-off and the search structured data. Changing a number meant finding
 * all twenty-four of them, and missing one meant publishing a number that
 * does not ring, which search engines happily index and callers happily
 * dial.
 *
 * Everything now reads from here, and `contactDetails.test.ts` fails if a
 * phone number is written out anywhere else.
 *
 * TURNING A LINE OFF
 * ------------------
 * Set `live: false` on a line and it disappears from the site, the emails
 * and the structured data, and the remaining copy falls back to email and
 * the booking link. Set it back to true, or change the number, and it
 * returns everywhere. That is one edit, in one file, with a test proving
 * nothing was left behind.
 */

export interface PhoneLine {
  /** As a human reads it. */
  label: string;
  /** E.164, which is what a dialler needs. */
  e164: string;
  /** Short name for the footer column. */
  name: string;
  /** False when the line is not in service. A line that is not live is never
   *  rendered, never linked and never published as structured data. */
  live: boolean;
}

export const PHONE_LINES: PhoneLine[] = [
  { name: "Toll-free", label: "(888) 331-3954", e164: "+18883313954", live: true },
  { name: "New York", label: "(646) 974-8446", e164: "+16469748446", live: true },
];

export const FAX = "(646) 559-6685";
export const CONTACT_EMAIL = "info@altitudefitlife.com";

export const ADDRESS_LINES = ["1185 Avenue of the Americas", "Suite 303", "New York, NY 10036"] as const;
export const ADDRESS_ONE_LINE = "1185 Avenue of the Americas, Suite 303, New York, NY 10036";

/** The lines that are actually in service. */
export const livePhoneLines = (): PhoneLine[] => PHONE_LINES.filter(line => line.live);

/** The number to print when only one is wanted, or null when none works. */
export const primaryPhone = (): PhoneLine | null => livePhoneLines()[0] ?? null;

export const hasPhone = (): boolean => livePhoneLines().length > 0;

/** "Please call us at <the live number>", or the email fallback when no line
 *  is in service. Used
 *  anywhere a sentence has to tell someone how to reach a human. */
export const reachUsSentence = (): string => {
  const phone = primaryPhone();
  return phone ? `Please call us at ${phone.label}.` : `Please email us at ${CONTACT_EMAIL}.`;
};

/** The same, as the tail of an email or post sign-off. */
export const contactLine = (): string => {
  const phone = primaryPhone();
  return phone ? `📞 ${phone.label}  ✉ ${CONTACT_EMAIL}` : `✉ ${CONTACT_EMAIL}`;
};

/** E.164 numbers for schema.org. An empty array is correct when no line is
 *  live: publishing a disconnected number to a search engine is worse than
 *  publishing none. */
export const schemaTelephones = (): string[] =>
  livePhoneLines().map(line => line.e164.replace(/^\+1(\d{3})(\d{3})(\d{4})$/, "+1-$1-$2-$3"));
